import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, AlertTriangle, Pill, ShieldAlert, Activity } from "lucide-react";
import MeCoreHero from "@/components/me/MeCoreHero";

export default function MemberHome({ targetUserId, member }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [insight, setInsight] = useState(null);
  const [insightBusy, setInsightBusy] = useState(false);

  const params = targetUserId ? { user_id: targetUserId } : {};

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError("");
    api
      .get("/zekaai/digital-twin", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]);

  async function generateInsight() {
    setInsightBusy(true);
    setInsight(null);
    try {
      const { data: ins } = await api.post("/brain/insight", {
        user_id: targetUserId,
        kind: "medical",
        language: user.language || "tr",
      });
      setInsight(ins);
    } catch (e) {
      const raw = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      const isUnavailable = e.response?.status === 503;
      setInsight({
        error: isUnavailable
          ? (user.language === "en"
              ? "Mi Brain AI service is temporarily unavailable. Please try again shortly."
              : "Mi Brain AI servisi şu anda kullanılamıyor. Lütfen kısa süre sonra tekrar deneyin.")
          : raw,
      });
    } finally {
      setInsightBusy(false);
    }
  }

  if (error) return <ErrorBlock message={error} />;
  if (!data) return <SkeletonBlock />;

  const score = data.stability_score;

  return (
    <div className="space-y-8" data-testid="member-dashboard">
      {/* ME Core Hub — only on the member's own dashboard (no targetUserId). */}
      {!targetUserId && <MeCoreHero language={user?.language || "tr"} />}

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            {t.twin.headline}
          </div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900" data-testid="member-name-heading">
            {data.member?.name || member?.name}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{t.twin.sub}</p>
        </div>
        <button
          type="button"
          onClick={generateInsight}
          disabled={insightBusy}
          className="mios-btn mios-btn-primary"
          data-testid="generate-insight-button"
        >
          <Sparkles className="h-4 w-4" />
          {insightBusy ? t.twin.explainRunning : t.twin.explainCta}
        </button>
      </header>

      <section className="grid gap-4 lg:grid-cols-3" data-testid="stability-row">
        <div className="mios-card lg:col-span-2">
          <div className="mios-card-header">
            <div>
              <div className="mios-card-sub">{t.twin.stability}</div>
              <div className="mt-1 flex items-baseline gap-3">
                <div className="text-5xl font-semibold tracking-tight text-slate-900" data-testid="stability-score-value">
                  {score?.value ?? "—"}
                </div>
                <BandPill band={score?.band} t={t} />
              </div>
            </div>
            <Activity className="h-6 w-6 text-slate-300" />
          </div>
          <div className="mios-card-body">
            <div className="mios-card-sub mb-2">{t.twin.reasons}</div>
            <ul className="space-y-1 text-sm text-slate-700">
              {(score?.reasons || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400" />
                  {r}
                </li>
              ))}
              {(!score?.reasons || score.reasons.length === 0) && (
                <li className="text-slate-400">—</li>
              )}
            </ul>
          </div>
        </div>
        <InsightCard insight={insight} busy={insightBusy} t={t} />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <ListCard
          title={t.twin.vitals}
          testid="vitals-card"
          items={data.vitals}
          render={(v) => (
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <div className="text-slate-700">{v.kind}</div>
              <div className="mono text-slate-900">
                {v.value} <span className="text-xs text-slate-500">{v.unit}</span>
              </div>
            </div>
          )}
          empty={t.twin.none}
        />
        <ListCard
          title={t.twin.labs}
          testid="labs-card"
          items={data.labs}
          render={(l) => (
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <div className="text-slate-700">{l.name}</div>
              <div className="flex items-center gap-2">
                <span className="mono text-slate-900">
                  {l.value} <span className="text-xs text-slate-500">{l.unit}</span>
                </span>
                <StatusPill status={l.status} />
              </div>
            </div>
          )}
          empty={t.twin.none}
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <ListCard
          title={t.twin.medications}
          icon={Pill}
          testid="medications-card"
          items={data.medications}
          render={(m) => (
            <div className="text-sm">
              <div className="font-medium text-slate-900">{m.name}</div>
              <div className="text-xs text-slate-500">
                {m.dose} · {m.frequency}
              </div>
            </div>
          )}
          empty={t.twin.none}
        />
        <ListCard
          title={t.twin.allergies}
          icon={ShieldAlert}
          testid="allergies-card"
          items={data.allergies}
          render={(a) => (
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-900">{a.substance}</span>
              <span className={`mios-pill ${severityPill(a.severity)}`}>{a.severity}</span>
            </div>
          )}
          empty={t.twin.none}
        />
        <ListCard
          title={t.twin.conditions}
          icon={AlertTriangle}
          testid="conditions-card"
          items={data.conditions}
          render={(c) => (
            <div className="text-sm">
              <div className="font-medium text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500">{c.status}</div>
            </div>
          )}
          empty={t.twin.none}
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-4" data-testid="lifestyle-row">
        <LifestyleCard
          label={t.twin.sleep}
          testid="lifestyle-sleep"
          value={avg(data.lifestyle.sleep, "duration_hours")}
          unit="sa"
        />
        <LifestyleCard
          label={t.twin.steps}
          testid="lifestyle-steps"
          value={avg(data.lifestyle.steps, "count", 0)}
          unit="/gün"
        />
        <LifestyleCard
          label={t.twin.mood}
          testid="lifestyle-mood"
          value={avg(data.lifestyle.mood, "score")}
          unit="/10"
        />
        <LifestyleCard
          label={t.twin.nutrition}
          testid="lifestyle-nutrition"
          value={avg(data.lifestyle.nutrition, "calories", 0)}
          unit="kcal"
        />
      </section>
    </div>
  );
}

function BandPill({ band, t }) {
  if (!band) return null;
  const label = t.twin.band[band] || band;
  const cls =
    band === "optimal"
      ? "mios-pill-normal"
      : band === "monitor"
      ? "mios-pill-warning"
      : "mios-pill-high";
  return (
    <span className={`mios-pill ${cls}`} data-testid={`stability-band-${band}`}>
      {label}
    </span>
  );
}

function StatusPill({ status }) {
  const cls =
    status === "high" ? "mios-pill-high" :
    status === "low" ? "mios-pill-low" :
    "mios-pill-normal";
  return <span className={`mios-pill ${cls}`}>{status}</span>;
}

function severityPill(sev) {
  if (sev === "severe") return "mios-pill-high";
  if (sev === "moderate") return "mios-pill-warning";
  return "mios-pill-normal";
}

function ListCard({ title, items, render, empty, testid, icon: Icon }) {
  return (
    <div className="mios-card" data-testid={testid}>
      <div className="mios-card-header">
        <div className="mios-card-title">{title}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-300" />}
      </div>
      <div className="mios-card-body">
        {items && items.length > 0 ? (
          <ul className="space-y-2.5">
            {items.slice(0, 6).map((it, i) => (
              <li key={it.id || i}>{render(it)}</li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-slate-400">{empty}</div>
        )}
      </div>
    </div>
  );
}

function LifestyleCard({ label, value, unit, testid }) {
  return (
    <div className="mios-card" data-testid={testid}>
      <div className="mios-card-body">
        <div className="mios-card-sub">{label}</div>
        <div className="mt-2 flex items-baseline gap-1.5">
          <div className="text-2xl font-semibold tracking-tight text-slate-900 mono">
            {value ?? "—"}
          </div>
          <div className="text-xs text-slate-500">{unit}</div>
        </div>
      </div>
    </div>
  );
}

function InsightCard({ insight, busy, t }) {
  return (
    <div className="mios-card" data-testid="insight-card">
      <div className="mios-card-header">
        <div className="mios-card-title">{t.twin.explain}</div>
        <Sparkles className="h-4 w-4 text-slate-300" />
      </div>
      <div className="mios-card-body">
        {busy && <div className="text-sm text-slate-500">{t.twin.explainRunning}</div>}
        {!busy && !insight && (
          <div className="text-sm text-slate-400">—</div>
        )}
        {insight?.error && <div className="text-sm text-red-700">{insight.error}</div>}
        {insight?.summary && (
          <>
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
              {insight.summary}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <span className="mios-pill mios-pill-normal mono">{insight.provider}</span>
              <span className="mono">{insight.model}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function avg(items, key, decimals = 1) {
  if (!items || items.length === 0) return null;
  const sum = items.reduce((s, x) => s + (Number(x[key]) || 0), 0);
  const v = sum / items.length;
  if (decimals === 0) return Math.round(v).toLocaleString();
  return v.toFixed(decimals);
}

function ErrorBlock({ message }) {
  return (
    <div className="mios-card" data-testid="page-error">
      <div className="mios-card-body text-sm text-red-700">{message}</div>
    </div>
  );
}

function SkeletonBlock() {
  return (
    <div className="space-y-4" data-testid="page-skeleton">
      <div className="h-8 w-64 animate-pulse rounded-md bg-slate-200" />
      <div className="grid gap-4 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="mios-card">
            <div className="mios-card-body">
              <div className="h-4 w-24 animate-pulse rounded bg-slate-200" />
              <div className="mt-3 h-8 w-32 animate-pulse rounded bg-slate-200" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
